import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BASE = import.meta.env.VITE_API_URL || 'https://apex-corporateweb-470928162af3.herokuapp.com';

interface FormFieldOption { label: string; value: string; }
interface FieldCondition { fieldId: string; operator: string; value?: string; }
interface FieldVisibility { action: 'show' | 'hide'; match: 'all' | 'any'; conditions: FieldCondition[]; }
interface ConditionRuleAction { action: 'show' | 'hide'; targetFieldIds: string[]; }
interface ConditionRule { id: string; enabled: boolean; match: 'all' | 'any'; conditions: FieldCondition[]; actions: ConditionRuleAction[]; }
interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  helpText?: string;
  placeholder?: string;
  options?: FormFieldOption[];
  defaultValue?: string;
  content?: string;
  dependsOn?: string;
  optionsByParent?: Record<string, FormFieldOption[]>;
  visibility?: FieldVisibility;
  hidden?: boolean;
  formula?: string;
  prefillKey?: string;
  accept?: string[];
  maxSizeMB?: number;
}

function isBlankVal(v: any): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}

function evalCondition(cond: FieldCondition, values: Record<string, any>): boolean {
  const v = values[cond.fieldId];
  const target = cond.value ?? '';
  switch (cond.operator) {
    case 'is_empty': return isBlankVal(v);
    case 'is_not_empty': return !isBlankVal(v);
    case 'equals':
      if (Array.isArray(v)) return v.map(String).includes(String(target));
      return String(v ?? '') === String(target);
    case 'not_equals':
      if (Array.isArray(v)) return !v.map(String).includes(String(target));
      return String(v ?? '') !== String(target);
    case 'contains':
      if (Array.isArray(v)) return v.map(String).includes(String(target));
      return String(v ?? '').toLowerCase().includes(String(target).toLowerCase());
    case 'gt': return Number(v) > Number(target);
    case 'lt': return Number(v) < Number(target);
    default: return true;
  }
}

function evalRuleGroup(match: 'all' | 'any', conditions: FieldCondition[], values: Record<string, any>): boolean {
  if (!conditions || conditions.length === 0) return false;
  const results = conditions.map((c) => evalCondition(c, values));
  return match === 'any' ? results.some(Boolean) : results.every(Boolean);
}

// Reads a rule's actions defensively: the current shape is `rule.actions`,
// but a rule from a form not yet re-saved since the multi-action change may
// still carry the old single `action`/`targetFieldIds` shape.
function ruleActionsFor(rule: any): ConditionRuleAction[] {
  if (Array.isArray(rule.actions) && rule.actions.length > 0) return rule.actions;
  if (rule.action && rule.targetFieldIds) return [{ action: rule.action, targetFieldIds: rule.targetFieldIds }];
  return [];
}

// Returns true when a field should be shown given the current form values and
// the form's condition rules. Legacy per-field `visibility` (old authoring
// model) is honored as-is for forms not yet re-saved through the builder;
// otherwise `hidden` sets the default state and every action of each matching
// rule is applied in order (later matching rules/actions win).
function isFieldVisible(field: FormField, values: Record<string, any>, rules?: ConditionRule[]): boolean {
  const vis = field.visibility;
  if (vis) {
    if (!vis.conditions || vis.conditions.length === 0) return true;
    const met = evalRuleGroup(vis.match, vis.conditions, values);
    return vis.action === 'show' ? met : !met;
  }
  let visible = !field.hidden;
  (rules || []).forEach((rule) => {
    if (!rule.enabled) return;
    if (!evalRuleGroup(rule.match, rule.conditions, values)) return;
    ruleActionsFor(rule).forEach((a) => {
      if (a.targetFieldIds.includes(field.id)) visible = a.action === 'show';
    });
  });
  return visible;
}

// Safe arithmetic evaluator (no eval). Supports + - * / and parentheses.
function safeEvalArithmetic(expr: string): number {
  const tokens = expr.match(/(\d+\.?\d*|\.\d+|[+\-*/()])/g);
  if (!tokens) return 0;
  const out: (number | string)[] = [];
  const ops: string[] = [];
  const prec: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };
  for (const t of tokens) {
    if (/^[\d.]/.test(t)) out.push(parseFloat(t));
    else if (t === '(') ops.push(t);
    else if (t === ')') {
      while (ops.length && ops[ops.length - 1] !== '(') out.push(ops.pop()!);
      ops.pop();
    } else {
      while (ops.length && prec[ops[ops.length - 1]] >= prec[t]) out.push(ops.pop()!);
      ops.push(t);
    }
  }
  while (ops.length) out.push(ops.pop()!);
  const stack: number[] = [];
  for (const t of out) {
    if (typeof t === 'number') stack.push(t);
    else {
      const b = stack.pop() ?? 0;
      const a = stack.pop() ?? 0;
      stack.push(t === '+' ? a + b : t === '-' ? a - b : t === '*' ? a * b : b === 0 ? 0 : a / b);
    }
  }
  const r = stack.pop() ?? 0;
  return isFinite(r) ? Math.round(r * 1e6) / 1e6 : 0;
}

function evaluateFormula(formula: string, values: Record<string, any>): number {
  if (!formula) return 0;
  const replaced = formula.replace(/\{([^}]+)\}/g, (_m, id) => {
    const v = values[String(id).trim()];
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? '0' : String(n);
  });
  return safeEvalArithmetic(replaced);
}

interface FormSection { id: string; title: string; description?: string; fields: FormField[]; }
interface PublicForm {
  _id: string;
  slug: string;
  title: string;
  description: string;
  sections: FormSection[];
  settings: { submitButtonText: string; successMessage: string; multiPage?: boolean };
  rules?: ConditionRule[];
}

// ---------------------------------------------------------------------------
// Minimal rich-text editor (contentEditable, no external deps)
// ---------------------------------------------------------------------------

// Strip styling that pasted content drags in (background highlight, font colors,
// font families, <font> tags) while keeping basic structural formatting.
const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'A', 'P', 'BR', 'UL', 'OL', 'LI', 'DIV', 'SPAN', 'H1', 'H2', 'H3', 'H4']);

function sanitizePastedHtml(html: string): string {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const walk = (node: Element) => {
    Array.from(node.children).forEach((child) => {
      const el = child as HTMLElement;
      walk(el);
      if (!ALLOWED_TAGS.has(el.tagName)) {
        const parent = el.parentNode;
        while (el.firstChild) parent?.insertBefore(el.firstChild, el);
        parent?.removeChild(el);
        return;
      }
      Array.from(el.attributes).forEach((attr) => {
        if (!(el.tagName === 'A' && attr.name === 'href')) el.removeAttribute(attr.name);
      });
    });
  };
  walk(tmp);
  return tmp.innerHTML;
}

function RichTextEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && ref.current) {
      ref.current.innerHTML = value || '';
      initialized.current = true;
    }
  }, [value]);

  const exec = useCallback((cmd: string, val?: string) => {
    document.execCommand(cmd, false, val ?? undefined);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  }, [onChange]);

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const clean = html ? sanitizePastedHtml(html) : text.replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, clean);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  return (
    <div className="form-page-rte">
      <div className="form-page-rte-toolbar">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}><b>B</b></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}><i>I</i></button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}><u>U</u></button>
        <span className="form-page-rte-sep" />
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>• List</button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>1. List</button>
        <span className="form-page-rte-sep" />
        <button type="button" onMouseDown={(e) => {
          e.preventDefault();
          const url = prompt('Enter URL:');
          if (url) exec('createLink', url);
        }}>Link</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        className="form-page-rte-content"
        onInput={() => { if (ref.current) onChange(ref.current.innerHTML); }}
        onPaste={handlePaste}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// File upload field — uploads to S3 via the public endpoint, stores metadata
// ---------------------------------------------------------------------------

function FileUploadInput({
  field,
  value,
  onChange,
  formSlug,
  hasError,
}: {
  field: FormField;
  value: any;
  onChange: (v: any) => void;
  formSlug: string;
  hasError: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploadError('');
    const maxMB = field.maxSizeMB ?? 10;
    if (file.size > maxMB * 1024 * 1024) {
      setUploadError(`File exceeds the ${maxMB}MB limit.`);
      return;
    }
    if (field.accept && field.accept.length > 0) {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      const ok = field.accept.some((a) => a.trim().toLowerCase() === ext);
      if (!ok) { setUploadError(`Allowed types: ${field.accept.join(', ')}`); return; }
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BASE}/api/forms/${formSlug}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (r.data?.success) onChange(r.data.data);
      else setUploadError(r.data?.message || 'Upload failed.');
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  if (value?.url) {
    return (
      <div className="form-page-file-done">
        <span className="form-page-file-name">📎 {value.name || 'Uploaded file'}</span>
        <button type="button" className="form-page-file-remove" onClick={() => onChange(undefined)}>Remove</button>
      </div>
    );
  }

  return (
    <div className={`form-page-file${hasError ? ' has-error' : ''}`}>
      <input
        ref={inputRef}
        type="file"
        accept={(field.accept || []).join(',')}
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
      />
      <button type="button" className="form-page-file-btn" onClick={() => inputRef.current?.click()} disabled={uploading}>
        {uploading ? 'Uploading…' : 'Choose file'}
      </button>
      <span className="form-page-file-hint">
        {field.accept && field.accept.length > 0 ? field.accept.join(', ') : 'Any file'} · max {field.maxSizeMB ?? 10}MB
      </span>
      {uploadError && <span className="form-page-file-error">{uploadError}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field input renderer
// ---------------------------------------------------------------------------

function FieldInput({
  field,
  value,
  onChange,
  error,
  allValues,
  formSlug,
}: {
  field: FormField;
  value: any;
  onChange: (v: any) => void;
  error?: string;
  allValues: Record<string, any>;
  formSlug: string;
}) {
  const cls = `form-page-input${error ? ' has-error' : ''}`;

  if (field.type === 'calculation') {
    const result = evaluateFormula(field.formula || '', allValues);
    return <div className="form-page-calc">{result}</div>;
  }

  if (field.type === 'file_upload') {
    return <FileUploadInput field={field} value={value} onChange={onChange} formSlug={formSlug} hasError={!!error} />;
  }

  if (field.type === 'section_heading') {
    return (
      <div className="form-page-section-heading">
        <h3>{field.label}</h3>
        {field.content && <p dangerouslySetInnerHTML={{ __html: field.content }} />}
      </div>
    );
  }
  if (field.type === 'description_block') {
    return <div className="form-page-description" dangerouslySetInnerHTML={{ __html: field.content || field.label }} />;
  }
  if (field.type === 'richText') {
    return <RichTextEditor value={value || ''} onChange={onChange} />;
  }
  if (field.type === 'long_text') {
    return <textarea className={cls} rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
  }

  // Cascading dropdown: options come from optionsByParent keyed by parent value
  if (field.type === 'dropdown') {
    let opts: FormFieldOption[];
    let disabled = false;
    if (field.dependsOn) {
      const parentValue = allValues[field.dependsOn];
      if (!parentValue) {
        opts = [];
        disabled = true;
      } else {
        opts = field.optionsByParent?.[parentValue] || [];
      }
    } else {
      opts = field.options || [];
    }

    return (
      <select className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">{disabled ? 'Select parent first…' : 'Select…'}</option>
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  // Cascading radio
  if (field.type === 'radio') {
    let opts: FormFieldOption[];
    let disabled = false;
    if (field.dependsOn) {
      const parentValue = allValues[field.dependsOn];
      if (!parentValue) { opts = []; disabled = true; }
      else opts = field.optionsByParent?.[parentValue] || [];
    } else {
      opts = field.options || [];
    }
    return (
      <div className="form-page-options">
        {disabled && <span className="form-page-cascade-hint">Select the parent field first.</span>}
        {opts.map((o) => (
          <label key={o.value} className="form-page-option">
            <input type="radio" name={field.id} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (field.type === 'checkboxes') {
    const opts = field.options || [];
    const checked: string[] = Array.isArray(value) ? value : [];
    const toggle = (v: string) => onChange(checked.includes(v) ? checked.filter((x) => x !== v) : [...checked, v]);
    return (
      <div className="form-page-options">
        {opts.map((o) => (
          <label key={o.value} className="form-page-option">
            <input type="checkbox" value={o.value} checked={checked.includes(o.value)} onChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    );
  }

  if (field.type === 'address_block') {
    const addr = value || {};
    const setAddr = (k: string, v: string) => onChange({ ...addr, [k]: v });
    return (
      <div className="form-page-address">
        <input className={cls} placeholder="Street address" value={addr.street || ''} onChange={(e) => setAddr('street', e.target.value)} />
        <div className="form-page-address-row">
          <input className={cls} placeholder="City" value={addr.city || ''} onChange={(e) => setAddr('city', e.target.value)} />
          <input className={cls} placeholder="State" value={addr.state || ''} onChange={(e) => setAddr('state', e.target.value)} />
          <input className={cls} placeholder="ZIP" value={addr.zip || ''} onChange={(e) => setAddr('zip', e.target.value)} />
        </div>
        <input className={cls} placeholder="Country" value={addr.country || ''} onChange={(e) => setAddr('country', e.target.value)} />
      </div>
    );
  }

  const inputType = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text';
  return <input className={cls} type={inputType} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
}

// ---------------------------------------------------------------------------
// Public form page
// ---------------------------------------------------------------------------

export default function FormPage() {
  const { slug } = useParams<{ slug: string }>();
  const [form, setForm] = useState<PublicForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${BASE}/api/forms/${slug}`)
      .then((r) => {
        if (r.data?.success) {
          const f: PublicForm = r.data.data;
          setForm(f);
          const initial: Record<string, any> = {};
          // Field-level default value (currently supported on dropdown fields)
          f.sections.flatMap((s) => s.fields).forEach((field) => {
            if (field.type === 'dropdown' && field.defaultValue) initial[field.id] = field.defaultValue;
          });
          // URL prefill: ?fieldId=value, ?prefillKey=value, or ?label-slug=value — overrides field defaults
          const params = new URLSearchParams(window.location.search);
          if (Array.from(params.keys()).length > 0) {
            f.sections.flatMap((s) => s.fields).forEach((field) => {
              const labelSlug = (field.label || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
              const raw = params.get(field.id) ?? (field.prefillKey ? params.get(field.prefillKey) : null) ?? params.get(labelSlug);
              if (raw !== null && raw !== undefined) {
                initial[field.id] = field.type === 'checkboxes' ? raw.split(',').map((s) => s.trim()) : raw;
              }
            });
          }
          if (Object.keys(initial).length > 0) setValues(initial);
        } else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const allFields = (form?.sections || []).flatMap((s) => s.fields);

  // Derive values with calculation fields resolved, for rendering/validation/submit.
  const computedValues = (() => {
    const v: Record<string, any> = { ...values };
    allFields.forEach((f) => { if (f.type === 'calculation') v[f.id] = evaluateFormula(f.formula || '', v); });
    return v;
  })();

  // Update a field's value and clear any now-stale dependents (transitively) so a
  // changed field never leaves invalid selections behind: cascading children whose
  // parent changed, and conditionally-hidden fields whose visibility just flipped off.
  const handleFieldChange = (fieldId: string, v: any) => {
    setValues((prev) => {
      const next = { ...prev, [fieldId]: v };
      let changed = [fieldId];
      while (changed.length) {
        const augmented = { ...next };
        allFields.forEach((f) => { if (f.type === 'calculation') augmented[f.id] = evaluateFormula(f.formula || '', augmented); });
        const nextChanged: string[] = [];
        allFields.forEach((f) => {
          // Never auto-clear the field the user just interacted with — it was
          // visible immediately before the click, so it can't have "just become
          // hidden" from its own action (this also guards against admin-authored
          // circular visibility rules, e.g. mutually-hiding sibling fields).
          if (f.id === fieldId) return;
          if (next[f.id] === undefined) return;
          const parentChanged = f.dependsOn && changed.includes(f.dependsOn);
          const nowHidden = !isFieldVisible(f, augmented, form?.rules);
          if (parentChanged || nowHidden) {
            delete next[f.id];
            nextChanged.push(f.id);
          }
        });
        changed = nextChanged;
      }
      return next;
    });
  };

  const validate = (fields: FormField[]) => {
    const errs: Record<string, string> = {};
    fields.forEach((f) => {
      if (!f.required) return;
      if (f.type === 'section_heading' || f.type === 'description_block' || f.type === 'calculation') return;
      // Skip fields hidden by conditional logic
      if (!isFieldVisible(f, computedValues, form?.rules)) return;
      // Skip cascading fields whose parent is not yet selected
      if (f.dependsOn && !computedValues[f.dependsOn]) return;
      const v = computedValues[f.id];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) || (f.type === 'file_upload' && !v?.url)) {
        errs[f.id] = `${f.label} is required.`;
      }
    });
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate(allFields);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    // Only submit values for fields currently visible — hidden conditional
    // fields must not leak stale answers into the submission.
    const visibleData: Record<string, any> = {};
    allFields.forEach((f) => {
      if (isFieldVisible(f, computedValues, form?.rules) && computedValues[f.id] !== undefined) visibleData[f.id] = computedValues[f.id];
    });
    try {
      const r = await axios.post(`${BASE}/api/forms/${slug}/submissions`, { data: visibleData, files: [] });
      if (r.data?.success) {
        setSubmitted(r.data.data?.successMessage || form?.settings.successMessage || 'Thank you!');
      } else {
        alert(r.data?.message || 'Submission failed. Please try again.');
      }
    } catch {
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="form-page-wrap"><div className="form-page-loading">Loading…</div></div>;
  if (notFound) return <div className="form-page-wrap"><div className="form-page-not-found"><h2>Form not found</h2><p>This form does not exist or is no longer accepting submissions.</p></div></div>;
  if (!form) return null;
  if (submitted) return <div className="form-page-wrap"><div className="form-page-success"><div className="form-page-success-icon">✓</div><p>{submitted}</p></div></div>;

  const totalPages = form.sections.length;
  const multiPage = !!form.settings.multiPage && totalPages > 1;
  const currentPage = Math.min(page, totalPages - 1);
  const sectionsToRender = multiPage ? [form.sections[currentPage]] : form.sections;
  const isLastPage = !multiPage || currentPage >= totalPages - 1;

  const goNext = () => {
    const errs = validate(form.sections[currentPage]?.fields || []);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setPage((p) => Math.min(totalPages - 1, p + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goBack = () => { setErrors({}); setPage((p) => Math.max(0, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className="form-page-wrap">
      <div className="form-page-card">
        <h1 className="form-page-title">{form.title}</h1>
        {form.description && <div className="form-page-intro" dangerouslySetInnerHTML={{ __html: form.description }} />}

        {multiPage && (
          <div className="form-page-progress">
            <div className="form-page-progress-bar"><div className="form-page-progress-fill" style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }} /></div>
            <span className="form-page-progress-text">Step {currentPage + 1} of {totalPages}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {sectionsToRender.map((section) => (
            <div key={section.id} className="form-page-section">
              {section.title && <h2 className="form-page-section-title">{section.title}</h2>}
              {section.description && <div className="form-page-section-desc" dangerouslySetInnerHTML={{ __html: section.description }} />}
              {section.fields.filter((field) => isFieldVisible(field, computedValues, form?.rules)).map((field) => (
                <div key={field.id} className="form-page-field">
                  {field.type !== 'section_heading' && field.type !== 'description_block' && (
                    <label className="form-page-label">
                      {field.label}
                      {field.required && <span className="form-page-required"> *</span>}
                    </label>
                  )}
                  {field.helpText && <p className="form-page-help">{field.helpText}</p>}
                  <FieldInput
                    field={field}
                    value={computedValues[field.id]}
                    onChange={(v) => handleFieldChange(field.id, v)}
                    error={errors[field.id]}
                    allValues={computedValues}
                    formSlug={slug || ''}
                  />
                  {errors[field.id] && <p className="form-page-error">{errors[field.id]}</p>}
                </div>
              ))}
            </div>
          ))}

          <div className="form-page-nav">
            {multiPage && currentPage > 0 && (
              <button type="button" className="form-page-btn-back" onClick={goBack}>← Back</button>
            )}
            {!isLastPage ? (
              <button type="button" className="form-page-submit" onClick={goNext}>Next →</button>
            ) : (
              <button type="submit" className="form-page-submit" disabled={submitting}>
                {submitting ? 'Submitting…' : form.settings.submitButtonText || 'Submit'}
              </button>
            )}
          </div>
        </form>
      </div>

      <style>{`
        .form-page-wrap { min-height: 100vh; background: #080c14; display: flex; align-items: flex-start; justify-content: center; padding: 4rem 1rem; }
        .form-page-card { background: #0a1628; border: 1px solid rgba(201,168,76,0.15); border-radius: 8px; padding: 2.5rem; width: 100%; max-width: 640px; }
        .form-page-title { font-family: 'Syne', sans-serif; font-size: 1.75rem; font-weight: 700; color: #f0f2f5; margin: 0 0 1rem; }
        .form-page-intro { color: #8a95a8; font-size: 0.95rem; line-height: 1.6; margin-bottom: 2rem; }
        .form-page-section { margin-bottom: 1.5rem; }
        .form-page-section-title { font-family: 'Syne', sans-serif; font-size: 1.05rem; font-weight: 700; color: #c9a84c; margin: 1.5rem 0 0.5rem; border-bottom: 1px solid rgba(201,168,76,0.15); padding-bottom: 0.5rem; }
        .form-page-section-desc { color: #8a95a8; font-size: 0.88rem; margin: 0 0 1rem; }
        .form-page-field { margin-bottom: 1.25rem; }
        .form-page-label { display: block; font-family: 'Syne', sans-serif; font-size: 0.92rem; font-weight: 600; color: #f0f2f5; margin-bottom: 0.4rem; }
        .form-page-required { color: #e74c3c; }
        .form-page-help { color: #8a95a8; font-size: 0.82rem; margin: 0 0 0.4rem; }
        .form-page-input { width: 100%; background: rgba(255,255,255,0.04); border: 1px solid rgba(201,168,76,0.2); border-radius: 4px; color: #f0f2f5; font-size: 0.92rem; padding: 0.65rem 0.85rem; outline: none; font-family: inherit; box-sizing: border-box; transition: border-color 0.15s; }
        .form-page-input:focus { border-color: rgba(201,168,76,0.5); }
        .form-page-input.has-error { border-color: rgba(231,76,60,0.6); }
        .form-page-input option { background: #0a1628; }
        .form-page-input:disabled { opacity: 0.45; cursor: not-allowed; }
        .form-page-error { color: #e74c3c; font-size: 0.8rem; margin: 0.3rem 0 0; }
        .form-page-options { display: flex; flex-direction: column; gap: 0.5rem; }
        .form-page-option { display: flex; align-items: center; gap: 0.6rem; cursor: pointer; color: #f0f2f5; font-size: 0.9rem; }
        .form-page-option input { accent-color: #c9a84c; width: 16px; height: 16px; }
        .form-page-cascade-hint { color: #8a95a8; font-size: 0.82rem; font-style: italic; }
        .form-page-address { display: flex; flex-direction: column; gap: 0.6rem; }
        .form-page-address-row { display: grid; grid-template-columns: 1fr 80px 100px; gap: 0.6rem; }
        .form-page-section-heading h3 { font-family: 'Syne', sans-serif; color: #c9a84c; font-size: 1rem; margin: 0.5rem 0 0.25rem; }
        .form-page-section-heading p { color: #8a95a8; font-size: 0.88rem; }
        .form-page-description { color: #8a95a8; font-size: 0.88rem; line-height: 1.6; }
        .form-page-submit { background: #c9a84c; color: #0a1628; border: none; border-radius: 4px; padding: 0.75rem 2rem; font-family: 'Syne', sans-serif; font-size: 0.85rem; font-weight: 700; letter-spacing: 0.1em; cursor: pointer; margin-top: 1rem; transition: opacity 0.15s; }
        .form-page-submit:hover { opacity: 0.85; }
        .form-page-submit:disabled { opacity: 0.5; cursor: not-allowed; }
        .form-page-loading, .form-page-not-found { padding: 4rem 2rem; text-align: center; color: #8a95a8; font-family: 'Syne', sans-serif; }
        .form-page-not-found h2 { color: #f0f2f5; font-size: 1.5rem; margin-bottom: 0.5rem; }
        .form-page-success { text-align: center; padding: 3rem 2rem; }
        .form-page-success-icon { font-size: 3rem; color: #34a853; margin-bottom: 1rem; }
        .form-page-success p { color: #f0f2f5; font-size: 1.1rem; font-family: 'Syne', sans-serif; }

        /* Rich text editor */
        .form-page-rte { border: 1px solid rgba(201,168,76,0.2); border-radius: 4px; overflow: hidden; }
        .form-page-rte-toolbar { display: flex; align-items: center; gap: 2px; padding: 0.4rem 0.6rem; background: rgba(255,255,255,0.03); border-bottom: 1px solid rgba(201,168,76,0.12); flex-wrap: wrap; }
        .form-page-rte-toolbar button { background: none; border: 1px solid transparent; color: #8a95a8; cursor: pointer; padding: 0.2rem 0.45rem; border-radius: 3px; font-size: 0.82rem; font-family: inherit; transition: background 0.15s, color 0.15s; }
        .form-page-rte-toolbar button:hover { background: rgba(201,168,76,0.12); color: #c9a84c; border-color: rgba(201,168,76,0.2); }
        .form-page-rte-sep { width: 1px; height: 16px; background: rgba(201,168,76,0.15); margin: 0 4px; }
        .form-page-rte-content { min-height: 120px; padding: 0.65rem 0.85rem; color: #f0f2f5; font-size: 0.92rem; line-height: 1.6; outline: none; background: rgba(255,255,255,0.04); }
        .form-page-rte-content:focus { background: rgba(255,255,255,0.05); }
        .form-page-rte-content a { color: #c9a84c; }
        /* Tailwind preflight strips list markers globally — restore them here and in all rendered rich-text */
        .form-page-rte-content ul, .form-page-intro ul, .form-page-description ul, .form-page-section-desc ul, .form-page-section-heading ul { list-style: disc outside; padding-left: 1.5rem; margin: 0.4rem 0; }
        .form-page-rte-content ol, .form-page-intro ol, .form-page-description ol, .form-page-section-desc ol, .form-page-section-heading ol { list-style: decimal outside; padding-left: 1.5rem; margin: 0.4rem 0; }
        .form-page-rte-content li, .form-page-intro li, .form-page-description li, .form-page-section-desc li, .form-page-section-heading li { margin: 0.2rem 0; }
        .form-page-intro a, .form-page-description a, .form-page-section-desc a { color: #c9a84c; }

        /* Multi-page progress + navigation */
        .form-page-progress { margin-bottom: 2rem; }
        .form-page-progress-bar { height: 5px; background: rgba(255,255,255,0.06); border-radius: 3px; overflow: hidden; }
        .form-page-progress-fill { height: 100%; background: #c9a84c; transition: width 0.3s ease; }
        .form-page-progress-text { display: block; margin-top: 0.5rem; font-family: 'Syne', sans-serif; font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; color: #8a95a8; }
        .form-page-nav { display: flex; align-items: center; gap: 0.75rem; margin-top: 1.25rem; }
        .form-page-btn-back { background: transparent; color: #c9a84c; border: 1px solid rgba(201,168,76,0.4); border-radius: 4px; padding: 0.75rem 1.5rem; font-family: 'Syne', sans-serif; font-size: 0.85rem; font-weight: 600; letter-spacing: 0.08em; cursor: pointer; transition: border-color 0.15s; }
        .form-page-btn-back:hover { border-color: #c9a84c; }

        /* Calculation field */
        .form-page-calc { background: rgba(201,168,76,0.08); border: 1px solid rgba(201,168,76,0.25); border-radius: 4px; color: #c9a84c; font-family: 'Syne', sans-serif; font-size: 1.15rem; font-weight: 700; padding: 0.65rem 0.85rem; }

        /* File upload */
        .form-page-file { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
        .form-page-file.has-error .form-page-file-btn { border-color: rgba(231,76,60,0.6); }
        .form-page-file-btn { background: rgba(255,255,255,0.04); color: #f0f2f5; border: 1px solid rgba(201,168,76,0.3); border-radius: 4px; padding: 0.6rem 1.1rem; font-family: inherit; font-size: 0.88rem; cursor: pointer; transition: border-color 0.15s; }
        .form-page-file-btn:hover { border-color: rgba(201,168,76,0.6); }
        .form-page-file-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .form-page-file-hint { color: #8a95a8; font-size: 0.78rem; }
        .form-page-file-error { color: #e74c3c; font-size: 0.8rem; flex-basis: 100%; }
        .form-page-file-done { display: flex; align-items: center; gap: 0.75rem; background: rgba(52,168,83,0.08); border: 1px solid rgba(52,168,83,0.25); border-radius: 4px; padding: 0.6rem 0.85rem; }
        .form-page-file-name { color: #f0f2f5; font-size: 0.9rem; }
        .form-page-file-remove { background: none; border: none; color: #e74c3c; cursor: pointer; font-size: 0.82rem; text-decoration: underline; }
      `}</style>
    </div>
  );
}
